import { Hint } from "igfd-ui";
import {
  type CustomContentProps,
  SnackbarProvider as NotistackSnackbarProvider,
  SnackbarContent,
  useSnackbar as useSnackbarNotistack,
} from "notistack";

import React, { forwardRef, useMemo } from "react";

type Snackbar = {
  success: (message: string) => void;
  error: (message: string) => void;
};

/**
 * A stripped down version of the useSnackbar from notistack with only success and error
 * @returns
 */
export const useSnackbar = (): Snackbar => {
  const { enqueueSnackbar } = useSnackbarNotistack();

  // Stable identity across renders: callers list this in effect dependency
  // arrays, so a new object each render would re-run those effects.
  return useMemo(
    () => ({
      success: (message: string) =>
        enqueueSnackbar(message, { variant: "success" }),
      error: (message: string) =>
        enqueueSnackbar(message, { variant: "error" }),
    }),
    [enqueueSnackbar]
  );
};

type HintProps = {
  message: string;
} & Omit<CustomContentProps, "message">;

// from https://github.com/iamhosseindhv/notistack/issues/440#issuecomment-955632581
// https://notistack.com/features/customization#custom-component
const SuccessHint = forwardRef<HTMLDivElement, HintProps>(
  ({ message }, ref) => (
    <SnackbarContent ref={ref}>
      <Hint hintValue={message} severity={"success"} />
    </SnackbarContent>
  )
);

const ErrorHint = forwardRef<HTMLDivElement, HintProps>(({ message }, ref) => (
  <SnackbarContent ref={ref}>
    <Hint hintValue={message} severity={"error"} />
  </SnackbarContent>
));

export const SnackbarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <NotistackSnackbarProvider
    maxSnack={3}
    autoHideDuration={3000}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    Components={{
      default: SuccessHint,
      success: SuccessHint,
      error: ErrorHint,
    }}
  >
    {children}
  </NotistackSnackbarProvider>
);
